import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../reusable/Input/Input';
import AuthSlider from '../../../reusable/AuthSlider/AuthSlider';
import { useRequestPasswordResetMutation } from '../../../store/api/authApi';
import { authErrorMessage } from '../../../store/api/authHelpers';
import { t } from '../../../i18n/i18n';
import '../SignIn/SignIn.scss';
import './ForgotPassword.scss';

const schema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

/**
 * Forgot password — the sign-in page's layout around one email field. Sends
 * `POST /password/reset-request/` as the old dashboard did; the link in the
 * email it triggers finishes the reset, so there is no second step here.
 */
export default function ForgotPassword() {
  const [requestReset, { isLoading }] = useRequestPasswordResetMutation();
  const [sentTo, setSentTo] = useState(null);
  const [failed, setFailed] = useState(null);

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: schema,
    onSubmit: async (values) => {
      const email = values.email.trim();
      setFailed(null);
      try {
        const data = await requestReset({ email }).unwrap();
        // The endpoint can answer 200 with the reason it refused in the body.
        if (data?.error?.message) {
          setFailed(data.error.message);
          return;
        }
        setSentTo(email);
      } catch (err) {
        setFailed(err?.data?.detail ?? err?.data?.error?.message ?? authErrorMessage(err));
      }
    },
  });

  const fieldError = (name) => (formik.touched[name] ? t(formik.errors[name]) : undefined);

  return (
    <div className="signin-page-split">
      <div className="signin-banner-side">
        <AuthSlider />
      </div>

      <div className="signin-form-side">
        <div className="signin-brand-logo">
          <img src="/images/Logo.png" alt={t('Alpha Capital Logo')} className="logo-img" />
        </div>

        <div className="signin-form-content">
          <div className="signin-form-header">
            <h1 className="signin-title">{t('Forgot password')}</h1>
            <p className="signin-description">
              {t('Enter the email address on your account and we’ll send you a link to reset your password.')}
            </p>
          </div>

          {sentTo ? (
            <div className="signin-form-body">
              <p className="forgot-sent" role="status">
                {t('We’ve sent a password reset link to {{email}}. Check your inbox and spam folder.', {
                  email: sentTo,
                })}
              </p>
              <Link to="/sign-in" className="signin-submit-button forgot-back">
                {t('Back to sign in')}
              </Link>
            </div>
          ) : (
            <form className="signin-form-body" onSubmit={formik.handleSubmit} noValidate>
              {failed && (
                <div className="signin-form-error" role="alert">
                  {t(failed)}
                </div>
              )}

              <Input
                label={t('Email')}
                type="email"
                name="email"
                placeholder={t('Enter your email')}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError('email')}
                disabled={isLoading}
                autoComplete="email"
              />

              <button type="submit" className="signin-submit-button" disabled={isLoading}>
                {isLoading ? t('Sending…') : t('Send reset link')}
              </button>

              <div className="signin-footer-text">
                {t('Remembered it?')}{' '}
                <Link to="/sign-in" className="signin-signup-link">
                  {t('Back to sign in')}
                </Link>
              </div>
            </form>
          )}
        </div>

        <footer className="signin-copyright">
          <span>
            © {new Date().getFullYear()} Alpha Capital Group. {t('All rights reserved.')}
          </span>
        </footer>
      </div>
    </div>
  );
}
